import React, { useEffect } from 'react';
import { Box, CircularProgress, Drawer, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ReactMarkdown from 'react-markdown';
import rehypeSlug from 'rehype-slug';

import { useContextualHelp } from '../../context/ContextualHelpContext';

// Panel de ayuda contextual -- Drawer DERECHO, misma estética oscura que
// ProfileDrawer.js (#0f1115, texto blanco, 500px / 100% en mobile). Se monta
// UNA sola vez cerca de la raíz de la app (ver AppRouter.jsx) y se controla
// enteramente vía useContextualHelp()/HelpIcon -- ningún caller necesita
// renderizar este componente de nuevo.
//
// rehype-slug le agrega un `id` (slug del texto) a cada heading que
// ReactMarkdown renderiza -- es lo que permite que HelpIcon pida un
// `anchor` (el mismo slug que usa el heading `## Nombre del campo` en el
// .md) y este Drawer haga scroll directo a esa sección sin tener que
// parsear/sluggear el Markdown a mano.
const ContextualHelpDrawer = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isOpen, isLoading, error, topic, anchor, content, closeHelp } = useContextualHelp();

  useEffect(() => {
    if (isLoading || !isOpen || !anchor) {
      return;
    }
    // Esperar al próximo frame para que ReactMarkdown ya haya montado los
    // headings con su id antes de intentar el scroll.
    const id = requestAnimationFrame(() => {
      document.getElementById(anchor)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    return () => cancelAnimationFrame(id);
  }, [isLoading, isOpen, anchor, content]);

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={closeHelp}
      sx={{
        '& .MuiDrawer-paper': {
          width: isMobile ? '100%' : 500,
          backgroundColor: 'var(--wb-bg-deep)',
          color: 'var(--wb-text-strong)',
          padding: 3,
          overflowY: 'auto'
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HelpOutlineIcon sx={{ color: 'var(--wb-blue)' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Ayuda contextual
          </Typography>
        </Box>
        <IconButton onClick={closeHelp} size="small" sx={{ color: 'var(--wb-x-text-9ca3af, #9ca3af)' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {isLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4 }}>
          <CircularProgress size={22} sx={{ color: 'var(--wb-blue)' }} />
          <Typography variant="body2" color="gray">
            Cargando ayuda{topic ? ` de "${topic}"` : ''}...
          </Typography>
        </Box>
      )}

      {!isLoading && error && (
        <Box
          sx={{
            backgroundColor: 'var(--wb-x-bg-f871711a, rgba(248, 113, 113, 0.1))',
            border: '1px solid var(--wb-x-border-f871714c, rgba(248, 113, 113, 0.3))',
            borderRadius: 2,
            p: 2
          }}
        >
          <Typography variant="body2" sx={{ color: 'var(--wb-danger-light)' }}>
            {error}
          </Typography>
        </Box>
      )}

      {!isLoading && !error && content && (
        <Box
          sx={{
            '& h1, & h2, & h3': { color: 'var(--wb-text-strong)', fontWeight: 600, mt: 3, mb: 1, scrollMarginTop: '16px' },
            '& h1:first-of-type': { mt: 0 },
            '& p': { color: 'var(--wb-x-text-d1d5db, #d1d5db)', lineHeight: 1.6, mb: 1.5 },
            '& ul, & ol': { color: 'var(--wb-x-text-d1d5db, #d1d5db)', pl: 3, mb: 1.5 },
            '& code': {
              backgroundColor: 'var(--wb-x-bg-1c1f26, #1c1f26)',
              borderRadius: 1,
              px: 0.5,
              fontSize: '0.85em'
            },
            '& a': { color: 'var(--wb-blue)' },
            '& blockquote': {
              borderLeft: '3px solid var(--wb-x-border-ffffff33, rgba(255,255,255,0.2))',
              pl: 2,
              ml: 0,
              color: 'var(--wb-x-text-9ca3af, #9ca3af)'
            }
          }}
        >
          <ReactMarkdown rehypePlugins={[rehypeSlug]}>{content}</ReactMarkdown>
        </Box>
      )}

      {!isLoading && !error && !content && (
        <Typography variant="body2" color="gray">
          No hay contenido de ayuda disponible para esta sección todavía.
        </Typography>
      )}
    </Drawer>
  );
};

export default ContextualHelpDrawer;
